/**
 * Touch tools.
 * 
 * There are two space areas, the view space (the area the user touches), and the coordinate space (the thing
 * that is being pinched, zoomed, moved and rotated).
 * 
 * Scale, translate then rotate.
 * 
 * @author Stephen Paul Hassall
 * @license https://CodeRunDebug.com/license.html
 * @web https://CodeRunDebug.com
 */
import Vector from "./vector.js";

export default class TouchTools {
    /**
     * Default constructor.
     */
    constructor() {
        // Set touch element
        this._touchElement = null;

        // Set default coordinate space parts
        this._coordinateSpaceX = 0.0;
        this._coordinateSpaceY = 0.0;
        this._coordinateSpaceScale = 1.0;
        this._coordinateSpaceRotate = 0.0;

        // Set touch count (1 or 2). 0 = pointer/mouse
        this._touchCount = -1;
    }

    /**
     * Get the current coordinate space translation X axis adjustment value.
     * @return {Number} The adjusted X amount.
     */
    get coordinateSpaceX() {
        // Return the coordinate space X value
        return this._coordinateSpaceX;
    }

    /**
     * Get the current coordinate space translation Y axis adjustment value.
     * @return {Number} The adjusted Y amount.
     */
    get coordinateSpaceY() {
        // Return the coordinate space Y value
        return this._coordinateSpaceY;
    }

    /**
     * Get the current coordinate space zoom/scale adjustment value.
     * @return {Number} The adjusted scale amount.
     */
    get coordinateSpaceScale() {
        // Return the coordinate space scale value
        return this._coordinateSpaceScale;
    }

    /**
     * Get the current coordinate space rotation adjustment value (in radians).
     * @return {Number} The adjusted rotate amount.
     */
    get coordinateSpaceRotate() {
        // Return the coordinate space rotate value
        return this._coordinateSpaceRotate;
    }
    
    /**
     * Touch start event. Call this when the user starts touching the view space element.
     * It can be just the one finger (to move the coordinate space only) or two fingers to
     * perform a full adjustment.
     * @param {Touch[]} touchList A list of touch object.
     */
    touchStart(touchList) {
        // Set default touch count
        this._touchCount = -1;

        // If touch count is not 1 or 2
        if (touchList.length !== 1 && touchList.length !== 2) return;

        // Set touch count
        this._touchCount = touchList.length;

        // Set start touch 1 points
        this._startTouch1X = touchList[0].clientX;
        this._startTouch1Y = touchList[0].clientY;

        // If 2 touches
        if (this._touchCount === 2) {
            // Set start touch 2 points
            this._startTouch2X = touchList[1].clientX;
            this._startTouch2Y = touchList[1].clientY;
        }

        // Save start coordinate values
        this._startCoordinateSpaceX = this._coordinateSpaceX;
        this._startCoordinateSpaceY = this._coordinateSpaceY;
        this._startCoordinateSpaceScale = this._coordinateSpaceScale;
        this._startCoordinateSpaceRotate = this._coordinateSpaceRotate;
    }

    /**
     * Touch move event. Call this when the user moves their fingers over the view space element
     * to adjust the coordinate space.
     * @param {Touch[]} touchList A list of touch object.
     */
    touchMove(touchList) {
        // If the touch counts do not match
        if (this._touchCount !== touchList.length) return;

        // Set end touch 1 points
        this._endTouch1X = touchList[0].clientX;
        this._endTouch1Y = touchList[0].clientY;

        // If 2 touches
        if (this._touchCount === 2) {
            // Set end touch 2 points
            this._endTouch2X = touchList[1].clientX;
            this._endTouch2Y = touchList[1].clientY;
        }

        // Update current coordinate value
        this._updateCoordinateSpace();
    }

    /**
     * Pointer down event, within the view space element.
     * @param {Object} event Information about the event.
     */
    pointerDown(event) {
        // If no touch element set
        if (this._touchElement === null) return;

        // If pointer type is not mouse
        if (event.pointerType !== 'mouse') return;

        // Set touch count to 0 (pointer/mouse)
        this._touchCount = 0;

        // Set start touch 1 points
        this._startTouch1X = event.x;
        this._startTouch1Y = event.y;

        // Save start coordinate values
        this._startCoordinateSpaceX = this._coordinateSpaceX;
        this._startCoordinateSpaceY = this._coordinateSpaceY;
        this._startCoordinateSpaceScale = this._coordinateSpaceScale;
        this._startCoordinateSpaceRotate = this._coordinateSpaceRotate;

        this._touchElement.setPointerCapture(event.pointerId);
    }

    /**
     * Pointer up event, within the view space element.
     * @param {Object} event Information about the event.
     */
    pointerUp(event) {
        // If no touch element set
        if (this._touchElement === null) return;

        // If pointer type is not mouse
        if (event.pointerType !== 'mouse') return;

        // Reset touch count
        this._touchCount = -1;

        // Release pointer capture
        this._touchElement.releasePointerCapture(event.pointerId);
    }

    /**
     * Pointer move event, within the view space element.
     * @param {Object} event Information about the event.
     */
    pointerMove(event) {
        // If no touch element set
        if (this._touchElement === null) return;

        // If pointer type is not mouse
        if (event.pointerType !== 'mouse') return;

        // If the touch counts is not 0 (pointer/mouse)
        if (this._touchCount !== 0) return;

        // Set end touch 1 points
        this._endTouch1X = event.x;
        this._endTouch1Y = event.y;

        // Update current coordinate value
        this._updateCoordinateSpace();
    }

    /**
     * Set the touch element.
     * @param {HTMLElement} touchElement The DOM element that the user can touch to change the coordinate space.
     */
    setTouchElement(touchElement) {
        // Set touch element
        this._touchElement = touchElement;
    }

    /**
     * Set the coordinate space starting adjustment.
     * @param {Number} x The translation along the x axis.
     * @param {Number} y The translation along the y axis.
     * @param {Number} scale The scaled amount.
     * @param {Number} rotate The rotation angle (in degrees).
     */
    setCoordinateSpace(x, y, scale, rotate) {
        // Set the starting coordinate space adjustments
        this._coordinateSpaceX = x;
        this._coordinateSpaceY = y;
        this._coordinateSpaceScale = scale;
        this._coordinateSpaceRotate = rotate;
    }

    /**
     * Update the coordinate space after the touch has moved. Use the starting and ending touch points
     * and calculate the new coordinate space adjustment values.
     */
    _updateCoordinateSpace() {
        // STEP 1: Get touch element dimensions (related to the viewport)
        this._touchElementRect = this._touchElement.getBoundingClientRect();
        
        // STEP 2: Convert the touch X/Y points (in client/element space) into view space vectors
        this._touchToViewSpace();

        // STEP 3: Workout center points
        this._workoutTouchCenter();

        // STEP 4: Workout the new coordinate space adjustments
        this._workoutCoordinateSpace();
    }

    /**
     * Convert the touch X/Y values (that are in the touch element) into the view space.
     * The view space is in pixels from the center of the touch element. Negative values
     * are the bottom, positive values at the top.
     */
    _touchToViewSpace() {
        // Convert touch 1 points into vectors in view space
        this._viewSpaceStartTouch1 = new Vector(
            this._touchToViewSpaceX(this._startTouch1X),
            this._touchToViewSpaceY(this._startTouch1Y)
        );
        this._viewSpaceEndTouch1 = new Vector(
            this._touchToViewSpaceX(this._endTouch1X),
            this._touchToViewSpaceY(this._endTouch1Y)
        );

        // If not two touches then stop here
        if (this._touchCount !== 2) return;

        // Convert touch 2 points into vectors in view space
        this._viewSpaceStartTouch2 = new Vector(
            this._touchToViewSpaceX(this._startTouch2X),
            this._touchToViewSpaceY(this._startTouch2Y)
        );
        this._viewSpaceEndTouch2 = new Vector(
            this._touchToViewSpaceX(this._endTouch2X),
            this._touchToViewSpaceY(this._endTouch2Y)
        );
    }

    /**
     * Convert the given touch X value (which is related to the client/viewport) into
     * the view space (center of the touch element).
     * @param {Number} touchX The touch X value.
     * @return {Number} The touch X value converted into a view space value.
     */
    _touchToViewSpaceX(touchX) {
        // Workout the view space X point
        const viewSpaceX = touchX - (this._touchElementRect.width / 2) - this._touchElementRect.x;

        // Return the view space X value
        return viewSpaceX;
    }

    /**
     * Convert the given touch Y value (which is related to the client/viewport) into
     * the view space. This is in pixels from the center of the touch element.
     * @param {Number} touchY The touch Y value.
     * @return {Number} The touch Y value converted into a view space value.
     */
    _touchToViewSpaceY(touchY) {
        // Workout the view space Y point
        const viewSpaceY = touchY - (this._touchElementRect.height / 2) - this._touchElementRect.y;

        // Return the view space Y value (mirror it so top is positive and bottom is negative)
        return -viewSpaceY;
    }

    /**
     * Workout the touch start/end center locations (in the view space).
     */
    _workoutTouchCenter() {
        // If pointer/mouse or only one touch
        if (this._touchCount === 0 || this._touchCount === 1) {
            // The center point will be the same as the touch point
            this._viewSpaceStartTouchCenter = this._viewSpaceStartTouch1.clone();
            this._viewSpaceEndTouchCenter = this._viewSpaceEndTouch1.clone();

            // Stop here
            return;
        }

        // Set view space center point for starting touches
        this._viewSpaceStartTouchCenter = new Vector(
            this._viewSpaceStartTouch1.x + ((this._viewSpaceStartTouch2.x - this._viewSpaceStartTouch1.x) / 2),
            this._viewSpaceStartTouch1.y + ((this._viewSpaceStartTouch2.y - this._viewSpaceStartTouch1.y) / 2)
        );

        // Set view space center point for ending touches
        this._viewSpaceEndTouchCenter = new Vector(
            this._viewSpaceEndTouch1.x + ((this._viewSpaceEndTouch2.x - this._viewSpaceEndTouch1.x) / 2),
            this._viewSpaceEndTouch1.y + ((this._viewSpaceEndTouch2.y - this._viewSpaceEndTouch1.y) / 2)
        );
    }

    /**
     * Workout the new coordinate space adjustments.
     */
    _workoutCoordinateSpace() {
        // If pointer/mouse or only one touch
        if (this._touchCount === 0 || this._touchCount === 1) {
            // Workout the touch movement
            const touchMovement = new Vector(
                this._viewSpaceEndTouchCenter.x - this._viewSpaceStartTouchCenter.x,
                this._viewSpaceEndTouchCenter.y - this._viewSpaceStartTouchCenter.y
            );

            // Scale the touch movement into the current coordinate space
            touchMovement.scale(1 / this._startCoordinateSpaceScale);

            // Adjust the coordinate space XY parts by the touch movement
            this._coordinateSpaceX = this._startCoordinateSpaceX + touchMovement.x;
            this._coordinateSpaceY = this._startCoordinateSpaceY + touchMovement.y;

            // Stop here
            return;
        }

        // Scale is based on the distance between the two points at the start compared to the end

        // Get start distance
        const startDistanceX = Math.abs(this._viewSpaceStartTouch1.x - this._viewSpaceStartTouch2.x);
        const startDistanceY = Math.abs(this._viewSpaceStartTouch1.y - this._viewSpaceStartTouch2.y);
        const startDistance = Math.sqrt((startDistanceX * startDistanceX) + (startDistanceY * startDistanceY));

        // Get end distance
        const endDistanceX = Math.abs(this._viewSpaceEndTouch1.x - this._viewSpaceEndTouch2.x);
        const endDistanceY = Math.abs(this._viewSpaceEndTouch1.y - this._viewSpaceEndTouch2.y);
        const endDistance = Math.sqrt((endDistanceX * endDistanceX) + (endDistanceY * endDistanceY));

        // Workout adjustment scale
        let adjustmentScale = endDistance / startDistance;

        // Rotation is based on the angle from (start touch 1/2) adjusted to angle to (end touch 1/2)

        // Workout the start angle (in radians)
        const startAngle = Math.atan2(
            (this._viewSpaceStartTouch1.x - this._viewSpaceStartTouch2.x),
            (this._viewSpaceStartTouch1.y - this._viewSpaceStartTouch2.y));

        // Workout the end angle (in radians)
        const endAngle = Math.atan2(
            (this._viewSpaceEndTouch1.x - this._viewSpaceEndTouch2.x),
            (this._viewSpaceEndTouch1.y - this._viewSpaceEndTouch2.y));

        // Workout the adjustment rotate (in radians)
        let adjustmentRotate = endAngle - startAngle;


        // STEP 1: Set coordinate space parts to the start values
        this._coordinateSpaceX = this._startCoordinateSpaceX;
        this._coordinateSpaceY = this._startCoordinateSpaceY;
        this._coordinateSpaceRotate = this._startCoordinateSpaceRotate;
        this._coordinateSpaceScale = this._startCoordinateSpaceScale;


        // STEP 2: Movement

        // Set start and end center points
        const startCenter = this._viewSpaceStartTouchCenter.clone();
        const endCenter = this._viewSpaceEndTouchCenter.clone();

        // Workout the movement adjustment made
        let movementAdjustment = new Vector(
            endCenter.x - startCenter.x,
            endCenter.y - startCenter.y);

        // Scale into coordinate space
        movementAdjustment.scale(1 / this._coordinateSpaceScale);

        // Adjust the coordinate space XY parts by the movement
        this._coordinateSpaceX += movementAdjustment.x;
        this._coordinateSpaceY += movementAdjustment.y;


        // STEP 3: Scale

        // Set from point
        let from = this._viewSpaceEndTouchCenter.clone();

        // Scale down into current coordinate space
        from.scale(1 / this._coordinateSpaceScale);

        // Set starting to point
        let to = from.clone();

        // Scale the to point
        to.scale(adjustmentScale);
        
        // Workout the adjustment made by scaling
        const scaleAdjustment = new Vector(
            from.x - to.x,
            from.y - to.y);

        // Scale into starting coordinate space
        scaleAdjustment.scale(1 / adjustmentScale);

        // Adjust the coordinate space XY parts by the scale
        this._coordinateSpaceX += scaleAdjustment.x;
        this._coordinateSpaceY += scaleAdjustment.y;

        // Reset the new coordinate space to take the scale adjustment into account
        this._coordinateSpaceScale *= adjustmentScale;


        // STEP 3: Rotate

        // Set from point
        from = this._viewSpaceEndTouchCenter.clone();

        // Scale down into current coordinate space
        from.scale(1 / this._coordinateSpaceScale);

        // Set starting to point
        to = from.clone();

        // Set coordinate space center
        const center = new Vector(this._coordinateSpaceX, this._coordinateSpaceY);

        // Perform the adjustment rotate (around the coordinate center)
        to.translate(-center.x, -center.y);
        to.rotate(adjustmentRotate);
        to.translate(center.x, center.y);

        // Workout the adjustment made by rotation
        let rotateAdjustment = new Vector(
            to.x - from.x,
            to.y - from.y);

        // Rotate into starting coordinate space
        rotateAdjustment.rotate(-adjustmentRotate);

        // Adjust the coordinate space XY parts by the rotation
        this._coordinateSpaceX += rotateAdjustment.x;
        this._coordinateSpaceY += rotateAdjustment.y;

        // Set the new coordinate space rotation to take rotate adjustment into account
        this._coordinateSpaceRotate += adjustmentRotate;
    }
}
